'use client'

import { useEffect, useState } from 'react'
import { useAuth } from '@/store/hooks'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import {
    MdArrowBack,
    MdSearch,
    MdClose,
    MdMessage,
    MdRefresh,
    MdPerson,
    MdFilterList,
    MdVisibility,
} from 'react-icons/md'
import {
    getLabMessages,
    processLabMessage,
    LabMessage,
    LabMessageStatus,
    LAB_MESSAGE_STATUS_LABELS,
    LAB_MESSAGE_STATUS_COLORS,
    LAB_MESSAGE_TYPE_LABELS,
} from '@/lib/api/health-integrations/lab-integration-api'

export default function LabMessagesPage() {
    const { user } = useAuth()
    const navigate = useNavigate()

    const [messages, setMessages] = useState<LabMessage[]>([])
    const [filteredMessages, setFilteredMessages] = useState<LabMessage[]>([])
    const [loading, setLoading] = useState(true)
    const [searchTerm, setSearchTerm] = useState('')
    const [statusFilter, setStatusFilter] = useState<LabMessageStatus | ''>('')
    const [processing, setProcessing] = useState<number | null>(null)
    const [selectedMessage, setSelectedMessage] = useState<LabMessage | null>(
        null
    )

    useEffect(() => {
        if (user?.companyId) {
            loadMessages()
        }
    }, [user?.companyId, statusFilter])

    useEffect(() => {
        if (searchTerm.trim()) {
            const filtered = messages.filter(
                message =>
                    message.messageId
                        ?.toLowerCase()
                        .includes(searchTerm.toLowerCase()) ||
                    message.client?.name
                        .toLowerCase()
                        .includes(searchTerm.toLowerCase()) ||
                    message.integration?.name
                        .toLowerCase()
                        .includes(searchTerm.toLowerCase())
            )
            setFilteredMessages(filtered)
        } else {
            setFilteredMessages(messages)
        }
    }, [searchTerm, messages])

    const loadMessages = async () => {
        setLoading(true)
        try {
            const filters: any = {}
            if (statusFilter) filters.status = statusFilter

            const response = await getLabMessages(filters)
            if (response.success) {
                setMessages(response.result)
                setFilteredMessages(response.result)
            } else {
                toast.error(response.error || 'Erro ao carregar mensagens')
            }
        } catch (error) {
            toast.error('Erro ao carregar mensagens')
        } finally {
            setLoading(false)
        }
    }

    const handleProcess = async (id: number) => {
        setProcessing(id)
        try {
            const response = await processLabMessage(id)
            if (response.success) {
                toast.success('Mensagem processada')
                loadMessages()
            } else {
                toast.error(response.error || 'Erro ao processar mensagem')
            }
        } catch (error) {
            toast.error('Erro ao processar mensagem')
        } finally {
            setProcessing(null)
        }
    }

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">
                    Carregando mensagens...
                </p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => navigate('/health-integrations/lab')}
                        className="btn btn-ghost btn-sm"
                    >
                        <MdArrowBack className="text-xl" />
                    </button>
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-lg">
                            <MdMessage className="text-2xl text-white" />
                        </div>
                        <div>
                            <h1 className="text-2xl font-bold text-base-content">
                                Mensagens Laboratoriais
                            </h1>
                            <p className="text-sm text-base-content/60">
                                {messages.length}{' '}
                                {messages.length === 1
                                    ? 'mensagem'
                                    : 'mensagens'}{' '}
                                recebidas
                            </p>
                        </div>
                    </div>
                </div>
                <button
                    onClick={loadMessages}
                    className="btn btn-ghost btn-sm gap-2"
                >
                    <MdRefresh className="text-xl" />
                    Atualizar
                </button>
            </div>

            <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-4">
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                    <div className="flex-1 relative">
                        <MdSearch className="absolute z-10 left-3 top-1/2 -translate-y-1/2 text-base-content/70 text-xl pointer-events-none z-10" />
                        <input
                            type="text"
                            placeholder="Buscar por ID, paciente ou integração..."
                            className="input input-bordered w-full pl-10"
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                        />
                    </div>

                    <div className="flex items-center gap-2">
                        <MdFilterList className="text-xl text-base-content/60" />
                        <select
                            className="select select-bordered"
                            value={statusFilter}
                            onChange={e =>
                                setStatusFilter(
                                    e.target.value as LabMessageStatus | ''
                                )
                            }
                        >
                            <option value="">Todos os status</option>
                            {Object.entries(LAB_MESSAGE_STATUS_LABELS).map(
                                ([value, label]) => (
                                    <option key={value} value={value}>
                                        {label}
                                    </option>
                                )
                            )}
                        </select>
                    </div>

                    {searchTerm && (
                        <button
                            type="button"
                            onClick={() => setSearchTerm('')}
                            className="btn btn-ghost btn-sm"
                        >
                            <MdClose className="text-lg" />
                        </button>
                    )}
                </div>
            </div>

            {filteredMessages.length === 0 ? (
                <div className="flex flex-col items-center justify-center min-h-[400px] gap-4 bg-base-100 rounded-xl border border-base-300/50 p-8">
                    <div className="w-20 h-20 rounded-full bg-base-200 flex items-center justify-center">
                        <MdMessage className="text-4xl text-base-content/30" />
                    </div>
                    <div className="text-center">
                        <h3 className="text-lg font-semibold text-base-content mb-1">
                            Nenhuma mensagem encontrada
                        </h3>
                        <p className="text-sm text-base-content/60">
                            {searchTerm || statusFilter
                                ? 'Tente ajustar seus filtros'
                                : 'Mensagens de laboratórios aparecerão aqui'}
                        </p>
                    </div>
                </div>
            ) : (
                <div className="bg-base-100 rounded-xl border border-base-300/50 overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="table">
                            <thead>
                                <tr>
                                    <th>Tipo</th>
                                    <th>Integração</th>
                                    <th>Paciente</th>
                                    <th>Status</th>
                                    <th>Data</th>
                                    <th>Ações</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredMessages.map(message => (
                                    <tr key={message.id} className="hover">
                                        <td>
                                            <span className="badge badge-outline">
                                                {
                                                    LAB_MESSAGE_TYPE_LABELS[
                                                        message.messageType
                                                    ]
                                                }
                                            </span>
                                        </td>
                                        <td>
                                            {message.integration?.name || '-'}
                                        </td>
                                        <td>
                                            {message.client ? (
                                                <div className="flex items-center gap-2">
                                                    <MdPerson className="text-lg text-base-content/60" />
                                                    <span>
                                                        {message.client.name}
                                                    </span>
                                                </div>
                                            ) : (
                                                <span className="text-base-content/50">
                                                    Não vinculado
                                                </span>
                                            )}
                                        </td>
                                        <td>
                                            <span
                                                className={`badge ${LAB_MESSAGE_STATUS_COLORS[message.status]}`}
                                            >
                                                {
                                                    LAB_MESSAGE_STATUS_LABELS[
                                                        message.status
                                                    ]
                                                }
                                            </span>
                                        </td>
                                        <td>
                                            {new Date(
                                                message.createdAt
                                            ).toLocaleString('pt-BR')}
                                        </td>
                                        <td>
                                            <div className="flex items-center gap-1">
                                                <button
                                                    onClick={() =>
                                                        setSelectedMessage(
                                                            message
                                                        )
                                                    }
                                                    className="btn btn-ghost btn-sm"
                                                    title="Ver detalhes"
                                                >
                                                    <MdVisibility className="text-lg" />
                                                </button>
                                                {(message.status ===
                                                    'RECEIVED' ||
                                                    message.status ===
                                                        'PARSED' ||
                                                    message.status ===
                                                        'MATCHED') && (
                                                    <button
                                                        onClick={() =>
                                                            handleProcess(
                                                                message.id
                                                            )
                                                        }
                                                        className="btn btn-ghost btn-sm"
                                                        title="Processar"
                                                        disabled={
                                                            processing ===
                                                            message.id
                                                        }
                                                    >
                                                        {processing ===
                                                        message.id ? (
                                                            <span className="loading loading-spinner loading-sm"></span>
                                                        ) : (
                                                            <MdRefresh className="text-lg" />
                                                        )}
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Message Detail Modal */}
            {selectedMessage && (
                <dialog open className="modal modal-open">
                    <div className="modal-box max-w-3xl">
                        <h3 className="font-bold text-lg mb-4">
                            Detalhes da Mensagem
                        </h3>

                        <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="text-sm text-base-content/60">
                                        Tipo
                                    </label>
                                    <p className="font-medium">
                                        {
                                            LAB_MESSAGE_TYPE_LABELS[
                                                selectedMessage.messageType
                                            ]
                                        }
                                    </p>
                                </div>
                                <div>
                                    <label className="text-sm text-base-content/60">
                                        Status
                                    </label>
                                    <p>
                                        <span
                                            className={`badge ${LAB_MESSAGE_STATUS_COLORS[selectedMessage.status]}`}
                                        >
                                            {
                                                LAB_MESSAGE_STATUS_LABELS[
                                                    selectedMessage.status
                                                ]
                                            }
                                        </span>
                                    </p>
                                </div>
                                <div>
                                    <label className="text-sm text-base-content/60">
                                        ID da Mensagem
                                    </label>
                                    <p className="font-medium">
                                        {selectedMessage.messageId || '-'}
                                    </p>
                                </div>
                                <div>
                                    <label className="text-sm text-base-content/60">
                                        Integração
                                    </label>
                                    <p className="font-medium">
                                        {selectedMessage.integration?.name ||
                                            '-'}
                                    </p>
                                </div>
                            </div>

                            {selectedMessage.client && (
                                <div>
                                    <label className="text-sm text-base-content/60">
                                        Paciente Vinculado
                                    </label>
                                    <p className="font-medium">
                                        {selectedMessage.client.name}
                                        {selectedMessage.client.cpf &&
                                            ` (${selectedMessage.client.cpf})`}
                                    </p>
                                </div>
                            )}

                            {selectedMessage.errorMessage && (
                                <div className="alert alert-error">
                                    <span>{selectedMessage.errorMessage}</span>
                                </div>
                            )}

                            <div>
                                <label className="text-sm text-base-content/60">
                                    Conteúdo Bruto
                                </label>
                                <pre className="bg-base-200 p-4 rounded-lg text-xs overflow-x-auto max-h-60">
                                    {selectedMessage.rawContent}
                                </pre>
                            </div>

                            {selectedMessage.parsedContent && (
                                <div>
                                    <label className="text-sm text-base-content/60">
                                        Conteúdo Processado
                                    </label>
                                    <pre className="bg-base-200 p-4 rounded-lg text-xs overflow-x-auto max-h-60">
                                        {JSON.stringify(
                                            selectedMessage.parsedContent,
                                            null,
                                            2
                                        )}
                                    </pre>
                                </div>
                            )}
                        </div>

                        <div className="modal-action">
                            <button
                                className="btn"
                                onClick={() => setSelectedMessage(null)}
                            >
                                Fechar
                            </button>
                        </div>
                    </div>
                    <form method="dialog" className="modal-backdrop">
                        <button onClick={() => setSelectedMessage(null)}>
                            close
                        </button>
                    </form>
                </dialog>
            )}
        </div>
    )
}
